import { translate, localHref } from "../i18n";
import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from '@phosphor-icons/react';

// The same ink stroke advances through connected segments. Each segment is
// mapped to its actual document position, so long sections cannot desync it.
function InkSegment({
  segment,
  scrollY,
  viewport,
  reduced
}) {
  const amount = useTransform(scrollY, [segment.start - viewport * .72, segment.end - viewport * .72], [0, 1]);
  const fade = useTransform(scrollY, [(segment.fadeStart || segment.end) - viewport * .72, (segment.fadeStart || segment.end) - viewport * .72 + (segment.fadeDistance || 1)], [1, 0]);
  return <motion.path d={segment.d} fill="none" stroke="currentColor" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" style={{
    pathLength: reduced ? 1 : amount,
    opacity: segment.final ? reduced ? 0 : fade : 1,
    stroke: segment.final ? '#08689d' : undefined
  }} />;
}
function positionPath(path, x, y, scale) {
  let coordinate = 0;
  return path.replace(/[MLCQ]|-?\d+(?:\.\d+)?/g, token => {
    if (/[MLCQ]/.test(token)) {
      coordinate = 0;
      return token;
    }
    const value = Number(token) * scale + (coordinate++ % 2 === 0 ? x : y);
    return String(Math.round(value * 10) / 10);
  });
}
export function ScrollThread({
  target
}) {
  const reduced = useReducedMotion();
  const {
    scrollY
  } = useScroll();
  const [drawing, setDrawing] = useState(null);
  useEffect(() => {
    const main = target.current;
    if (!main) return;
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const bounds = main.getBoundingClientRect();
        const width = main.clientWidth;
        const top = bounds.top + window.scrollY;
        const nodes = Array.from(main.querySelectorAll('.ph-intro,#prodotti,#servizi,#questo-mese,#storia'));
        const edge = Math.max(12, (width - 1320) / 2 - 65);
        const mobile = width < 641;
        const scale = mobile ? .44 : .58;
        const types = ['idea', 'paper', 'book', 'gift', 'book', 'paper', 'envelope'];
        const ends = {
          idea: [300, 179],
          paper: [300, 179],
          book: [300, 193],
          gift: [300, 143],
          envelope: [300, 180]
        };
        const starts = {
          idea: 180,
          paper: 180,
          book: 176,
          gift: 171,
          envelope: 184
        };
        const segments = [];
        let previous = {
          x: width - edge,
          y: Math.max(5, nodes[0].getBoundingClientRect().top - bounds.top - 100)
        };
        nodes.forEach((node, i) => {
          const sectionY = node.getBoundingClientRect().top - bounds.top;
          const kind = types[i];
          const x = i % 2 === 0 ? edge : width - edge - 300 * scale;
          const y = sectionY + 8;
          const begin = {
            x,
            y: y + starts[kind] * scale
          };
          // Broad, full-width waves: the stroke travels through the composition,
          // instead of following a vertical rail with a small bend at each section.
          const span = begin.y - previous.y;
          const direction = begin.x < previous.x ? -1 : 1;
          const travel = Math.abs(begin.x - previous.x);
          const middleX = (previous.x + begin.x) / 2;
          const middleY = previous.y + span * .5;
          const swell = Math.min(mobile ? 110 : 240, Math.max(90, span * .32));
          segments.push({
            d: `M ${previous.x} ${previous.y} C ${previous.x + direction * travel * .34} ${previous.y + swell} ${middleX - direction * travel * .2} ${middleY + swell} ${middleX} ${middleY} C ${middleX + direction * travel * .2} ${middleY - swell} ${begin.x} ${begin.y - swell} ${begin.x} ${begin.y}`,
            start: top + previous.y,
            end: top + begin.y
          });
          segments.push({
            d: positionPath(MARKS[kind], x, y, scale),
            start: top + begin.y,
            end: top + y + 300 * scale
          });
          previous = {
            x: x + ends[kind][0] * scale,
            y: y + ends[kind][1] * scale
          };
        });
        const art = main.querySelector('.ph-finale-product');
        if (art) {
          const r = art.getBoundingClientRect();
          const ax = r.left - bounds.left;
          const ay = r.top - bounds.top;
          const k = r.width / 1536;
          const bx = ax + 489 * k;
          const by = ay + 227 * k;
          const sceneStart = top + ay - window.innerHeight * .90;
          const duration = window.innerHeight * .55;
          const arrival = sceneStart + window.innerHeight * .72;
          segments.push({
            d: `M ${previous.x} ${previous.y} C ${width * .8} ${previous.y + 180} ${ax - 80} ${by - 180} ${bx} ${by}`,
            start: top + previous.y,
            end: arrival
          });
          segments.push({
            d: positionPath(FINAL_OUTLINE, ax, ay, k),
            start: arrival,
            end: sceneStart + duration * .45 + window.innerHeight * .72,
            final: true,
            fadeStart: sceneStart + duration * .55 + window.innerHeight * .72,
            fadeDistance: duration * .35
          });
        }
        const textAreas = Array.from(main.querySelectorAll('h1,h2,h3,h4,p,a,button,label,input,textarea,select,li,.ph-journey-stages span,.ph-personal-signature span,.ph-christmas-card')).flatMap(element => {
          let x = 0,
            y = 0,
            node = element;
          while (node && node !== main) {
            x += node.offsetLeft || 0;
            y += node.offsetTop || 0;
            node = node.offsetParent;
          }
          if (node !== main) {
            const rect = element.getBoundingClientRect();
            x = rect.left - bounds.left;
            y = rect.top - bounds.top;
          }
          const width = element.offsetWidth;
          const height = element.offsetHeight;
          if (!width || !height) return [];
          return [{
            x: x - 9,
            y: y - 7,
            width: width + 18,
            height: height + 14
          }];
        });
        setDrawing({
          segments,
          textAreas,
          width,
          height: main.scrollHeight,
          viewport: window.innerHeight,
          mobile
        });
      });
    };
    // Geometry only changes when the layout changes. Recalculating every scroll
    // frame forced repeated DOM reads and React renders, especially on phones.
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure, {
      once: true
    });
    measure();
    const settled = setTimeout(measure, 1400);
    document.fonts?.ready?.then(measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
      clearTimeout(settled);
      cancelAnimationFrame(frame);
    };
  }, [target]);
  if (!drawing) return null;
  if (drawing.mobile) return <svg className="ph-scroll-thread" viewBox={`0 0 ${drawing.width} ${drawing.height}`} preserveAspectRatio="none" aria-hidden="true"><g>{drawing.segments.map((segment, i) => <InkSegment key={i} segment={segment} scrollY={scrollY} viewport={drawing.viewport} reduced={reduced} />)}</g></svg>;
  return <svg className="ph-scroll-thread" viewBox={`0 0 ${drawing.width} ${drawing.height}`} preserveAspectRatio="none" aria-hidden="true"><defs><mask id="print-thread-text-clearance" maskUnits="userSpaceOnUse" x="0" y="0" width={drawing.width} height={drawing.height}><rect width={drawing.width} height={drawing.height} fill="white" />{drawing.textAreas.map((area, i) => <rect key={i} {...area} rx="4" fill="black" />)}</mask></defs><g mask="url(#print-thread-text-clearance)">{drawing.segments.map((segment, i) => <InkSegment key={i} segment={segment} scrollY={scrollY} viewport={drawing.viewport} reduced={reduced} />)}</g></svg>;
}
export function SketchJourney() {
  const artRef = useRef(null);
  const reduced = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: artRef,
    offset: ['start 90%', 'start 35%']
  });
  const opacity = useTransform(scrollYProgress, [.55, .9], [0, 1]);
  return <section id="dalla-linea" className="ph-journey"><div className="ph-wrap ph-journey-grid">
 <div className="ph-journey-copy"><p className="ph-label">{translate("DAL DISEGNO AL PRODOTTO FINITO")}</p><h2>{translate("Prima un segno.")}<br /><span>{translate("Poi, qualcosa di tuo.")}</span></h2><p><strong>{translate("Dalla tua idea a un prodotto da tenere tra le mani.")}</strong><br />{translate("Un tratto alla volta, il disegno diventa la composizione che hai visto all\u2019inizio. Poi arrivano il colore, la carta e le finiture: il progetto \xE8 pronto per essere stampato.")}</p><a className="ph-journey-cta" href={localHref("#contatti")}>{translate("Raccontaci cosa vuoi realizzare ")}<ArrowUpRight size={22} /></a></div>
 <div className="ph-journey-art"><div ref={artRef} className="ph-finale-product"><svg width="0" height="0" aria-hidden="true" style={{
            position: 'absolute'
          }}><defs><clipPath id="print-products-cutout" clipPathUnits="objectBoundingBox"><path transform="scale(0.000651041667 0.0009765625)" d="M489 227 L523 196 C688 149 864 57 933 123 C993 52 1159 29 1283 16 L1526 357 L1526 365 C1380 393 1192 438 1115 495 L1093 500 C1021 490 748 581 587 627 Z M644 695 L866 590 L948 675 L949 753 L729 843 L647 752 Z M967 616 L1301 442 L1536 663 L1536 939 L1390 1024 L1287 1024 L967 637 L978 631 Z" /></clipPath></defs></svg><motion.img src="/images/print-editorial-branded.webp" alt={translate("Brochure e biglietti Pedrazzini: il disegno diventa la composizione fotografica della home")} width="1536" height="1024" loading="lazy" style={{
            opacity: reduced ? 1 : opacity,
            clipPath: 'url(#print-products-cutout)'
          }} /></div></div>
 </div></section>;
}

// Restrained editorial drafting: folded stock, bound pages, calendar, envelope.
// Entry/exit coordinates are shared with the continuous scroll route above.
const MARKS = {
  idea: 'M 0 180 C 38 180 72 166 72 118 C 72 73 105 42 150 42 C 195 42 228 73 228 118 C 228 151 211 168 192 188 C 180 200 178 211 178 220 L 122 220 C 122 201 115 192 105 182 C 83 159 72 143 72 118 M 122 234 L 178 234 M 134 248 L 166 248 M 137 220 L 137 143 L 163 143 L 163 220 M 228 118 C 264 118 266 179 300 179',
  paper: 'M 0 180 C 34 180 62 163 62 130 L 62 66 C 62 54 72 44 84 44 L 193 44 C 200 44 205 47 210 52 L 237 79 C 242 84 244 90 244 98 L 244 221 C 244 233 234 243 222 243 L 84 243 C 72 243 62 233 62 221 L 62 130 M 195 45 L 195 80 C 195 89 200 94 209 94 L 244 94 M 95 126 L 210 126 M 95 154 L 210 154 M 95 182 L 173 182 M 244 166 C 262 166 277 179 300 179',
  book: 'M 0 176 C 25 176 43 168 43 145 L 43 76 C 43 64 52 56 64 56 L 116 56 C 136 56 151 65 151 80 C 151 65 166 56 186 56 L 238 56 C 250 56 259 64 259 76 L 259 215 C 259 224 253 230 244 230 L 186 230 C 165 230 155 237 151 244 C 147 237 137 230 116 230 L 58 230 C 49 230 43 224 43 215 L 43 145 M 151 80 L 151 244 M 72 99 L 124 99 M 72 127 L 124 127 M 72 155 L 111 155 M 179 99 L 230 99 M 179 127 L 230 127 M 179 155 L 217 155 M 259 177 C 276 177 281 193 300 193',
  gift: 'M 0 171 C 35 171 60 152 60 125 L 60 74 C 60 63 69 54 80 54 L 224 54 C 235 54 244 63 244 74 L 244 224 C 244 235 235 244 224 244 L 80 244 C 69 244 60 235 60 224 L 60 125 M 60 103 L 244 103 M 103 37 L 103 72 M 201 37 L 201 72 M 93 141 L 115 141 M 142 141 L 164 141 M 191 141 L 213 141 M 93 178 L 115 178 M 142 178 L 164 178 M 191 178 L 213 178 M 93 215 L 115 215 M 142 215 L 164 215 M 244 143 C 265 143 277 143 300 143',
  envelope: 'M 0 184 C 30 184 50 163 50 142 L 50 94 C 50 82 60 72 72 72 L 228 72 C 240 72 250 82 250 94 L 250 206 C 250 218 240 228 228 228 L 72 228 C 60 228 50 218 50 206 L 50 142 M 55 84 L 137 150 C 145 157 155 157 163 150 L 245 84 M 56 219 L 118 163 M 244 219 L 182 163 M 250 170 C 268 170 281 180 300 180'
};

// Contours traced in the original hero photograph's 1536 × 1024 coordinates.
const FINAL_OUTLINE = 'M 489 227 L 587 626 C 767 566 1027 478 1105 491 C 1191 430 1395 391 1526 357 L 1283 16 C 1122 34 987 44 933 123 C 869 61 748 118 645 155 L 489 227 M 933 123 C 987 219 1041 358 1105 491 M 502 218 L 599 610 C 773 553 1020 457 1098 480 M 515 208 L 612 592 M 587 626 L 1107 500 L 1526 365 M 525 197 L 613 570 C 868 549 1020 327 782 175 C 735 145 687 147 645 155 M 964 96 L 1111 375 C 1172 243 1306 214 1392 257 L 1259 20 M 645 155 C 700 280 829 316 1066 405 M 1022 254 C 1057 145 1210 99 1308 110 M 644 695 L 866 590 L 948 675 L 726 798 L 644 695 M 644 705 L 726 809 L 948 688 M 645 720 L 726 823 L 948 702 M 646 736 L 728 840 L 949 717 M 726 798 L 728 840 M 782 757 C 727 681 847 623 905 689 M 817 612 L 860 666 M 967 616 L 1301 442 L 1536 663 M 967 616 L 1287 1024 M 1536 924 L 1358 1024 M 976 641 L 1266 1024 M 1074 554 C 1240 550 1480 704 1264 965';
